import { useCallback, useEffect, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { getMediaDimensions } from 'utils/helpers'
import { validateMediaFileAsync } from '../../../Media/media'
import { validateMediaFile } from '../../useCarouselUploadStore'
import { CAROUSEL_GOOGLE_RATIO, carouselTargetsGoogle } from '../../carouselGoogleMedia'
import {
  getCarouselPlacementUploadErrorKey,
  getCarouselRequiredPlacementType,
  getPlacementTypeFromFile,
} from '../../carouselPlacement'

export const useInfoCard = ({ image, images = [], uploadStore, platforms }) => {
  const { t } = useTranslation()
  const targetsGoogle = carouselTargetsGoogle(platforms)

  const validateInfoCardMedia = useCallback(async (file) => {
    if (!file) return null

    // Google rejects video in a carousel card outright — not even a YouTube
    // reference is accepted there, unlike the single-ad path.
    if (targetsGoogle && file.type.startsWith('video/')) {
      return t('Recommendations.campaign.setup.carouselAd.errors.googleVideo', {
        fileName: file.name,
      })
    }

    if (file.type.startsWith('image/')) {
      const dimensions = await getMediaDimensions(file)
      if (dimensions?.width < 600 || dimensions?.height < 600) {
        return t('Recommendations.campaign.setup.carouselAd.errors.imageSize')
      }
    }

    if (file.type.startsWith('video/')) {
      const videoDuration = await new Promise((resolve, reject) => {
        const video = document.createElement('video')
        const sourceUrl = URL.createObjectURL(file)

        video.preload = 'metadata'
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(sourceUrl)
          resolve(video.duration)
        }
        video.onerror = () => {
          URL.revokeObjectURL(sourceUrl)
          reject(new Error('Failed to read video metadata'))
        }
        video.src = sourceUrl
      })

      if (videoDuration > 2 * 60) {
        return t('Recommendations.campaign.setup.carouselAd.errors.videoDuration', {
          fileName: file.name,
        })
      }
    }

    if (targetsGoogle) {
      const googleError = await validateMediaFileAsync(file, {
        isGoogleSelected: true,
        googleExactRatios: [CAROUSEL_GOOGLE_RATIO],
        targetRatio: CAROUSEL_GOOGLE_RATIO,
        requiredSlotRatios: [CAROUSEL_GOOGLE_RATIO],
        t,
      })
      if (googleError) return googleError
    }

    const incomingType = await getPlacementTypeFromFile(file)
    const requiredType = getCarouselRequiredPlacementType(images, { excludeCardId: image?.id })
    const placementErrorKey = getCarouselPlacementUploadErrorKey(incomingType, requiredType)
    if (placementErrorKey) {
      return t(`Recommendations.campaign.setup.carouselAd.errors.${placementErrorKey}`)
    }

    return null
  }, [image?.id, images, t, targetsGoogle])

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles?.length || !image?.id) return

    const cardId = image.id
    const selectedFile = acceptedFiles[0]
    if (!validateMediaFile(selectedFile)) return

    const validationError = await validateInfoCardMedia(selectedFile)
    if (validationError) {
      uploadStore.setUploadErrorByCard((prev) => ({ ...prev, [cardId]: validationError }))
      return
    }

    // Completion is reported via uploadStore.completedUploadByCard, consumed
    // by CarouselAd's own effect — not a callback here (see
    // useCarouselUploadStore.jsx's own comment for why).
    await uploadStore.uploadCardMedia(cardId, selectedFile)
  }, [image?.id, uploadStore, validateInfoCardMedia])

  const cancelUpload = useCallback(() => {
    const cardId = image?.id
    if (!cardId) return
    uploadStore.cancelUpload(cardId)
  }, [image?.id, uploadStore])

  const { isMediaLoading, mediaProgress, uploadError } = uploadStore.getCardUploadState(image?.id)

  const prevImageIdRef = useRef(image?.id)
  useEffect(() => {
    const prevImageId = prevImageIdRef.current
    if (prevImageId && prevImageId !== image?.id) {
      uploadStore.clearUploadError(prevImageId)
    }
    prevImageIdRef.current = image?.id
  }, [image?.id, uploadStore])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png, video/*',
    multiple: false,
  })

  return {
    getRootProps,
    getInputProps,
    isMediaLoading,
    mediaProgress,
    uploadError,
    cancelUpload,
  }
}
